<template>
  <BaseSection :config="sectionConfig">
    <div>
      <p
        v-if="!appearanceStore.account.password.isEdited"
        class="section__info"
      >
        &#9679;&#9679;&#9679;&#9679;&#9679;&#9679;
      </p>
      <MyForm
        v-else
        :state="form"
        :schema="schema"
        class="section__form"
        @submit="onSubmit"
      >
        <div class="section__fields">
          <MyInput
            v-model="form.oldPassword"
            name="oldPassword"
            caption="@Old Password"
            autocomplete="new-password"
            type="password"
            :custom-error="passwordInvalid"
          />
          <MyInput
            v-model="form.password"
            name="password"
            caption="@New Password"
            autocomplete="new-password"
            type="password"
          />
          <MyInput
            v-model="form.passwordConfirmation"
            name="passwordConfirmation"
            caption="@Password confirmation"
            autocomplete="new-password"
            type="password"
          />
        </div>
        <Actions :is-disabled="isDisabled" :on-cancel="onCancel" />
      </MyForm>
    </div>
  </BaseSection>
</template>

<script setup>
import { useAppearanceStore } from "~/store/appearanceStore.js";
import BaseSection from "~/modules/account/ui/pages/Profile/BaseSection.vue";
import { z } from "zod";
import MyForm from "~/nuxt_modules/common/ui/components/form/MyForm.vue";
import MyInput from "~/nuxt_modules/common/ui/components/form/MyInput.vue";
import {
  successfulModal,
  useModalStore,
} from "~/modules/shared/modals/store/modalStore.js";
import Actions from "~/modules/account/ui/pages/Profile/Actions.vue";
import { FORBIDDEN_ERROR } from "~/nuxt_modules/common/constants/index.js";
import { useUserStore } from "~/store/userStore.js";

const appearanceStore = useAppearanceStore();
const userStore = useUserStore();
const modalStore = useModalStore();

const passwordInvalid = ref(null);

const sectionConfig = {
  name: "password",
  title: "@Password",
  image: "account-password",
};

const successfulData = {
  title: _T("@Successful change profile title"),
  description: _T("@Successful change profile description"),
  isWithTimeout: true,
};

const initialForm = {
  oldPassword: undefined,
  password: undefined,
  passwordConfirmation: undefined,
};

const form = reactive({ ...initialForm });

const schema = z
  .object({
    oldPassword: z.string("@Entry field required"),
    password: z
      .string(_T("@Entry field required"))
      .min(6, `${_T("@Minimum password length")} 6`)
      .regex(/\d/, _T("@The password must contain at least one number")),
    passwordConfirmation: z.string(_T("@Entry field required")),
  })
  .superRefine((data, ctx) => {
    if (data.password !== data.passwordConfirmation) {
      ctx.addIssue({
        path: ["passwordConfirmation"],
        message: _T("@Password mismatch"),
      });
    }
  });

const isDisabled = computed(
  () => JSON.stringify(initialForm) === JSON.stringify(form),
);

const onSubmit = async (data) => {
  await userStore
    .updateAccount({
      oldPassword: data.oldPassword,
      newPassword: data.password,
    })
    .then(async () => {
      await userStore.fetch();
      onCancel();
      modalStore.toggleModal(successfulModal, successfulData);
    })
    .catch((error) => {
      if (FORBIDDEN_ERROR.ACCOUNT_PASSWORD_INVALID === error.data?.code) {
        passwordInvalid.value = _T("@Incorrect password");
      }
    });
};

function onCancel() {
  appearanceStore.account.password.isEdited = false;
  passwordInvalid.value = null;
  Object.assign(form, initialForm);
}
</script>
